/**
 * EnvironmentalReportsPage — V2 main orchestrator for Environmental Reports.
 *
 * Supports 4 report types (Temperature, Humidity, Custom Low Battery, Full Battery)
 * with Summary/Detailed view toggle, filter drawer, sort, column filters,
 * pagination, and CSV/PDF export.
 *
 * Replaces V1 EnvironmentReports.jsx with clean V2 architecture.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    updateSessionCampus,
    getUsername,
} from '../../../services/session.js';
import { iAmHigherAuthority, formatDateIso, formatDateTime12Hour, getToday, getWeekRange, getMonthRange, getQuarterRange } from '../../../libs.jsx';
import { exportWellnessCsv, exportWellnessPdf } from '../../../services/csvExport.js';
import { fetchEnvironmentalReport, fetchEnvironmentalFilterOptions } from './api/environmentalApi.js';

import EnvironmentalTable, { COLUMN_DEFS_MAP } from './components/EnvironmentalTable.jsx';
import EnvironmentalFilterDrawer from './components/EnvironmentalFilterDrawer.jsx';
import ScheduleReportDrawer from '../alert-details/components/ScheduleReportDrawer.jsx';
import { fetchFrequencies, createSchedule } from '../scheduled/api/scheduledApi.js';

import usePersistedPerPage from '../../../hooks/usePersistedPerPage.js';

import './environmental-reports-page.css';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TIME_RANGES = [
    { id: 1, label: 'Today', getRange: () => { const d = getToday(); d.setHours(0,0,0,0); const e = new Date(); return [d, e]; } },
    { id: 2, label: 'Yesterday', getRange: () => { const d = getToday(-1); d.setHours(0,0,0,0); const e = getToday(-1); e.setHours(23,59,59,999); return [d, e]; } },
    { id: 3, label: 'This Week', getRange: () => { const [s] = getWeekRange(0); s.setHours(0,0,0,0); return [s, new Date()]; } },
    { id: 4, label: 'This Month', getRange: () => { const [s] = getMonthRange(0); s.setHours(0,0,0,0); return [s, new Date()]; } },
    { id: 5, label: 'This Quarter', getRange: () => { const [s] = getQuarterRange(0); s.setHours(0,0,0,0); return [s, new Date()]; } },
    { id: 6, label: 'Last Week', getRange: () => { const [s, e] = getWeekRange(-1); s.setHours(0,0,0,0); e.setHours(23,59,59,999); return [s, e]; } },
    { id: 7, label: 'Last Month', getRange: () => { const [s, e] = getMonthRange(-1); s.setHours(0,0,0,0); e.setHours(23,59,59,999); return [s, e]; } },
    { id: 8, label: 'Last Quarter', getRange: () => { const [s, e] = getQuarterRange(-1); s.setHours(0,0,0,0); e.setHours(23,59,59,999); return [s, e]; } },
];

const REPORT_TYPES = [
    { value: 1, label: 'Temperature' },
    { value: 2, label: 'Humidity' },
    { value: 3, label: 'Custom Low Battery' },
    { value: 4, label: 'Full Battery' },
];


const NUMERIC_SORT_KEYS = [
    'current_temperature', 'avg_temperature', 'max_temperature', 'min_temperature',
    'current_humidity', 'avg_humidity', 'max_humidity', 'min_humidity',
    'current_battery', 'avg_battery', 'max_battery', 'min_battery', 'battery_voltage',
];

function emptyReportFilters() {
    return {
        areas: [],
        areaTypes: [],
        residents: [],
        users: [],
        eventTypes: [],
        reasons: [],
    };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const EnvironmentalReportsPage = () => {
    // -- Session --
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => {
        if (!session?.campusData || !campusId) return null;
        return session.campusData[campusId.toString()] ?? null;
    }, [session, campusId]);
    const tzOffset = campusConfig?.tz_offset;

    // -- Report type --
    const [environTypeId, setEnvironTypeId] = useState(1);
    const isFullBattery = environTypeId === 4;

    // -- Report state --
    const [status, setStatus] = useState('init');
    const [reportData, setReportData] = useState([]);
    const [summaryData, setSummaryData] = useState([]);
    const [overLimit, setOverLimit] = useState(false);
    const [csvUrl, setCsvUrl] = useState('');

    // -- Time range --
    const [rangeId, setRangeId] = useState(2);
    const [rangeName, setRangeName] = useState('Yesterday');
    const [customRange, setCustomRange] = useState(false);
    const [start, setStart] = useState(() => { const d = getToday(-1); d.setHours(0,0,0,0); return d; });
    const [end, setEnd] = useState(() => { const d = getToday(-1); d.setHours(23,59,59,999); return d; });
    const [startDisplay, setStartDisplay] = useState('');
    const [endDisplay, setEndDisplay] = useState('');

    // -- Report filters --
    const [reportFilters, setReportFilters] = useState(emptyReportFilters);
    const [filterOptions, setFilterOptions] = useState({});
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // -- Schedule drawer --
    const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
    const [frequencies, setFrequencies] = useState([]);

    // -- View mode --
    const [viewMode, setViewMode] = useState('summary');

    // -- Table state --
    const [search, setSearch] = useState('');
    const [columnFilters, setColumnFilters] = useState({});
    const [sortColumn, setSortColumn] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();

    // -- Active column defs --
    const activeColumnDefs = useMemo(() => {
        return COLUMN_DEFS_MAP[environTypeId]?.[viewMode] || [];
    }, [environTypeId, viewMode]);

    const [visibleColumns, setVisibleColumns] = useState(() =>
        (COLUMN_DEFS_MAP[1]?.summary || []).map((c) => c.key)
    );

    // -- UI --
    const [toast, setToast] = useState(null);
    const shouldRerunRef = useRef(false);
    const [timePopoverOpen, setTimePopoverOpen] = useState(false);
    const timePopoverRef = useRef(null);
    const pendingAutoRunRef = useRef(false);

    const showToast = useCallback((message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    // ======================================================================
    // Update visible columns when report type or view mode changes
    // ======================================================================
    useEffect(() => {
        const cols = COLUMN_DEFS_MAP[environTypeId]?.[viewMode] || [];
        setVisibleColumns(cols.map((c) => c.key));
        setColumnFilters({});
        setSearch('');
        setPage(1);
        if (cols.length > 0) {
            setSortColumn(cols[0].key);
            setSortOrder('asc');
        }
    }, [environTypeId, viewMode]);

    // ======================================================================
    // Load filter options on mount / campus change
    // ======================================================================
    useEffect(() => {
        if (!campusId) return;
        fetchEnvironmentalFilterOptions(campusId).then(setFilterOptions);
        fetchFrequencies().then((d) => setFrequencies(Array.isArray(d) ? d : []));
    }, [campusId]);

    // ======================================================================
    // Outside click / Escape to close time popover
    // ======================================================================
    useEffect(() => {
        if (!timePopoverOpen) return;
        const handleClick = (e) => {
            if (timePopoverRef.current && !timePopoverRef.current.contains(e.target)) {
                setTimePopoverOpen(false);
            }
        };
        const handleKey = (e) => {
            if (e.key === 'Escape') setTimePopoverOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [timePopoverOpen]);

    // ======================================================================
    // Time range helpers
    // ======================================================================
    const handleTimeRange = useCallback((range) => {
        const [s, e] = range.getRange();
        setStart(s);
        setEnd(e);
        setRangeId(range.id);
        setRangeName(range.label);
        setCustomRange(false);
    }, []);

    const handleCustomToggle = useCallback(() => {
        setCustomRange(true);
        setRangeId(0);
        setRangeName('Custom');
    }, []);

    const handlePopoverCustom = useCallback(() => {
        setTimePopoverOpen(false);
        handleCustomToggle();
        setFilterDrawerOpen(true);
    }, [handleCustomToggle]);

    // ======================================================================
    // Report type change
    // ======================================================================
    const handleReportTypeChange = useCallback((e) => {
        const newType = parseInt(e.target.value, 10);
        setEnvironTypeId(newType);

        if (newType === 4) {
            // Full Battery: force Summary + Today
            setViewMode('summary');
            const todayRange = TIME_RANGES.find((r) => r.id === 1);
            if (todayRange) {
                const [s, en] = todayRange.getRange();
                setStart(s);
                setEnd(en);
                setRangeId(1);
                setRangeName('Today');
                setCustomRange(false);
            }
        } else {
            setViewMode('summary');
        }

        setSearch('');
        setColumnFilters({});
        setPage(1);

        if (status !== 'init') {
            shouldRerunRef.current = true;
        }
    }, [status]);

    // ======================================================================
    // View mode change
    // ======================================================================
    const handleViewModeChange = useCallback((mode) => {
        setViewMode(mode);
        setSearch('');
        setColumnFilters({});
        setPage(1);
    }, []);

    // ======================================================================
    // Run Report
    // ======================================================================
    const handleRunReport = useCallback(async () => {
        setStatus('loading');
        setOverLimit(false);

        const now = new Date();
        const endToUse = end > now ? now : end;

        const stD = formatDateIso(start);
        const enD = formatDateIso(endToUse);

        setStartDisplay(formatDateTime12Hour(start));
        setEndDisplay(formatDateTime12Hour(endToUse));

        const filterPayload = {
            areas: (reportFilters.areas || []).join(','),
            areaTypes: (reportFilters.areaTypes || []).join(','),
            residents: (reportFilters.residents || []).join(','),
            users: (reportFilters.users || []).join(','),
            eventTypes: (reportFilters.eventTypes || []).join(','),
            reasons: (reportFilters.reasons || []).join(','),
        };

        const result = await fetchEnvironmentalReport({
            campusId,
            username: getUsername(),
            startDate: stD,
            endDate: enD,
            environTypeId,
            filters: filterPayload,
        });

        if (result.ErrorCode) {
            const errMap = {
                11: 'Report timed out. Contact your customer success manager for large reports.',
                10: 'An unknown error occurred. Please contact customer success if the problem persists.',
                12: 'Request unauthorized. Service may be unavailable.',
            };
            showToast(errMap[result.ErrorCode] || 'Report request failed.', 'error');
            setStatus('init');
            return;
        }

        if (result.overlimit) {
            setOverLimit(true);
            setCsvUrl(result.url || '');
            showToast('Report exceeds 5,000 events. Details will need to be downloaded.', 'warning');
        } else {
            setOverLimit(false);
            setCsvUrl(result.url || '');
        }

        setReportData(result.records || []);
        setSummaryData(result.summary || []);
        setPage(1);
        setStatus('ready');
    }, [start, end, campusId, environTypeId, reportFilters, showToast]);

    // ======================================================================
    // Re-run after filter apply / type change
    // ======================================================================
    useEffect(() => {
        if (shouldRerunRef.current) {
            shouldRerunRef.current = false;
            handleRunReport();
        }
    }, [reportFilters, environTypeId, handleRunReport]);

    // ======================================================================
    // Auto-run after quick time range selection
    // ======================================================================
    useEffect(() => {
        if (pendingAutoRunRef.current) {
            pendingAutoRunRef.current = false;
            handleRunReport();
        }
    });

    const handleQuickTimeRange = useCallback((range) => {
        handleTimeRange(range);
        pendingAutoRunRef.current = true;
        setTimePopoverOpen(false);
    }, [handleTimeRange]);

    // ======================================================================
    // Campus change
    // ======================================================================
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        setCampusId(val);
        updateSessionCampus(val);
        setStatus('init');
        setReportData([]);
        setSummaryData([]);
    }, []);

    // ======================================================================
    // Data pipeline
    // ======================================================================
    const activeData = useMemo(() => {
        const raw = viewMode === 'summary' ? summaryData : reportData;
        return Array.isArray(raw) ? raw : [];
    }, [viewMode, summaryData, reportData]);

    const searchedData = useMemo(() => {
        if (!search) return activeData;
        const term = search.toLowerCase();
        return activeData.filter((row) =>
            (row.resident_name || '').toLowerCase().includes(term) ||
            (row.area_name || '').toLowerCase().includes(term) ||
            (row.device_name || '').toLowerCase().includes(term) ||
            (row.manufacture_uuid || '').toLowerCase().includes(term) ||
            (row.descriptions || '').toLowerCase().includes(term)
        );
    }, [activeData, search]);

    const columnFilteredData = useMemo(() => {
        const active = Object.entries(columnFilters).filter(([, vals]) => vals.length > 0);
        if (active.length === 0) return searchedData;
        return searchedData.filter((row) =>
            active.every(([key, vals]) => vals.includes(row[key] != null ? String(row[key]) : ''))
        );
    }, [searchedData, columnFilters]);

    const sortedData = useMemo(() => {
        if (!sortColumn) return columnFilteredData;
        return [...columnFilteredData].sort((a, b) => {
            let aVal = a[sortColumn];
            let bVal = b[sortColumn];
            if (aVal == null) aVal = '';
            if (bVal == null) bVal = '';
            if (NUMERIC_SORT_KEYS.includes(sortColumn)) {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
                return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
            }
            const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
            return sortOrder === 'asc' ? cmp : -cmp;
        });
    }, [columnFilteredData, sortColumn, sortOrder]);

    const totalFiltered = sortedData.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
    const safePage = Math.min(page, totalPages);
    const paginatedData = useMemo(() => {
        const startIdx = (safePage - 1) * perPage;
        return sortedData.slice(startIdx, startIdx + perPage);
    }, [sortedData, safePage, perPage]);
    const showingStart = totalFiltered === 0 ? 0 : (safePage - 1) * perPage + 1;
    const showingEnd = Math.min(safePage * perPage, totalFiltered);

    const pageNumbers = useMemo(() => {
        const pages = [];
        const max = 5;
        let startP = Math.max(1, safePage - Math.floor(max / 2));
        let endP = Math.min(totalPages, startP + max - 1);
        if (endP - startP < max - 1) startP = Math.max(1, endP - max + 1);
        for (let i = startP; i <= endP; i++) pages.push(i);
        return pages;
    }, [safePage, totalPages]);

    // ======================================================================
    // Handlers
    // ======================================================================
    const handleSort = useCallback((colKey) => {
        setSortColumn((prev) => {
            if (prev === colKey) { setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc')); return prev; }
            setSortOrder('asc');
            return colKey;
        });
        setPage(1);
    }, []);

    const handleColumnFilterChange = useCallback((columnKey, selectedValues) => {
        setColumnFilters((prev) => ({ ...prev, [columnKey]: selectedValues }));
        setPage(1);
    }, []);

    const handleClearColumnFilters = useCallback(() => { setColumnFilters({}); setPage(1); }, []);

    const handleApplyFilters = useCallback((filters) => {
        setReportFilters(filters);
        setFilterDrawerOpen(false);
        if (status === 'ready') shouldRerunRef.current = true;
    }, [status]);

    const handleClearReportFilters = useCallback(() => {
        setReportFilters(emptyReportFilters());
        if (status === 'ready') shouldRerunRef.current = true;
    }, [status]);

    const reportFilterChips = useMemo(() => {
        const chips = [];
        const labelMap = { areas: 'Areas', areaTypes: 'Area Types', residents: 'Residents', users: 'Users', eventTypes: 'Event Types', reasons: 'Reasons' };
        Object.entries(reportFilters).forEach(([key, vals]) => {
            if (Array.isArray(vals) && vals.length > 0) chips.push({ key, label: `${labelMap[key] || key}: ${vals.length}` });
        });
        return chips;
    }, [reportFilters]);

    const activeReportFilterCount = reportFilterChips.length;

    const exportColumnDefs = useMemo(() =>
        activeColumnDefs.filter((col) => visibleColumns.includes(col.key)),
    [activeColumnDefs, visibleColumns]);

    const handleExportCsv = useCallback(() => {
        const filename = `environmental-${REPORT_TYPES.find((r) => r.value === environTypeId)?.label?.toLowerCase() || environTypeId}-${viewMode}.csv`;
        exportWellnessCsv(sortedData, exportColumnDefs, filename);
    }, [sortedData, exportColumnDefs, environTypeId, viewMode]);

    const handleExportPdf = useCallback(() => {
        const repLabel = REPORT_TYPES.find((r) => r.value === environTypeId)?.label || 'Environmental';
        const dateRange = startDisplay && endDisplay ? `${startDisplay} – ${endDisplay}` : '';
        const filename = `environmental-${repLabel.toLowerCase()}-${viewMode}.pdf`;
        exportWellnessPdf(sortedData, exportColumnDefs, `Environmental: ${repLabel} (${viewMode})`, dateRange, filename);
    }, [sortedData, exportColumnDefs, environTypeId, viewMode, startDisplay, endDisplay]);

    // Schedule
    const environTypeIdMap = { 1: 6, 2: 7, 3: 8, 4: 12 };
    const handleScheduleSave = useCallback(async (payload) => {
        const result = await createSchedule(payload, { campusId, userId: getUserId(), clientId: getSessionId() });
        if (result[0]) { showToast('Schedule created successfully.'); setScheduleDrawerOpen(false); }
        else { showToast(result[1] || 'Failed to create schedule.', 'error'); }
    }, [campusId, showToast]);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ar-page">
                    {/* Toast */}
                    {toast ? (
                        <div className={`ar-toast ar-toast--${toast.type}`} role="status" aria-live="polite">
                            <span>{toast.message}</span>
                            <button onClick={() => setToast(null)} className="ar-toast__close" aria-label="Dismiss">{'\u2715'}</button>
                        </div>
                    ) : null}

                    {/* PAGE HEADER */}
                    <div className="ar-page-header">
                        <div className="ar-page-header__inner">
                            <div className="ar-breadcrumb-row">
                                <div className="ar-breadcrumb">
                                    <span className="ar-breadcrumb__text">Reports</span>
                                    <svg className="ar-icon-md ar-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                    <span className="ar-breadcrumb__current">Environment</span>
                                </div>
                                <div className="ar-campus-row">
                                    <label htmlFor="er-campus-selector" className="ar-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select id="er-campus-selector" value={campusId} onChange={handleCampusChange} className="ar-campus-select">
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ar-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>

                            <div className="ar-title-row">
                                <div className="ar-title-row__left">
                                    <h1 className="ar-page-title">Environment</h1>
                                    <select className="er-report-type-select" value={environTypeId} onChange={handleReportTypeChange} title="Report type">
                                        {REPORT_TYPES.map((rt) => (
                                            <option key={rt.value} value={rt.value}>{rt.label}</option>
                                        ))}
                                    </select>
                                    <div className="ar-time-badge-wrap" ref={timePopoverRef}>
                                        <span
                                            className={`ar-time-summary${timePopoverOpen ? ' ar-time-summary--open' : ''}`}
                                            onClick={() => setTimePopoverOpen((p) => !p)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTimePopoverOpen((p) => !p); } }}
                                            aria-expanded={timePopoverOpen}
                                            aria-haspopup="true"
                                        >
                                            <i className="fe fe-clock" style={{ marginRight: '4px', fontSize: 'var(--font-size-xs)' }} />
                                            {customRange
                                                ? `${formatDateTime12Hour(start)} – ${formatDateTime12Hour(end)}`
                                                : rangeName}
                                            <svg className="ar-time-summary__chevron" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                            </svg>
                                        </span>

                                        {timePopoverOpen ? (
                                            <div className="ar-time-popover">
                                                <div className="ar-time-popover__grid">
                                                    {TIME_RANGES.map((range) => (
                                                        <button
                                                            key={range.id}
                                                            type="button"
                                                            className={`ar-time-popover__btn${rangeId === range.id && !customRange ? ' ar-time-popover__btn--active' : ''}`}
                                                            onClick={() => handleQuickTimeRange(range)}
                                                        >
                                                            {range.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="ar-time-popover__divider" />
                                                <button
                                                    type="button"
                                                    className={`ar-time-popover__custom${customRange ? ' ar-time-popover__custom--active' : ''}`}
                                                    onClick={handlePopoverCustom}
                                                >
                                                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                                                    </svg>
                                                    Custom Range…
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="ar-title-row__actions">
                                    {!customRange ? (
                                        <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={() => setScheduleDrawerOpen(true)}>
                                            <i className="fe fe-calendar" style={{ marginRight: '4px' }} />Schedule Report
                                        </button>
                                    ) : null}
                                    <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={() => setFilterDrawerOpen(true)} style={{ position: 'relative' }}>
                                        <i className="fe fe-sliders" style={{ marginRight: '4px' }} />
                                        Report Settings
                                        {activeReportFilterCount > 0 ? (<span className="ar-filter-badge">{activeReportFilterCount}</span>) : null}
                                    </button>
                                    <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={handleRunReport} disabled={status === 'loading'}>
                                        <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                        {status === 'loading' ? 'Running...' : 'Run Report'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* View toggle — hidden for Full Battery */}
                    {status === 'ready' && !overLimit && !isFullBattery ? (
                        <div className="ar-subtab-bar">
                            <div className="pp-subtab-toggle">
                                <button className={`pp-subtab-btn${viewMode === 'summary' ? ' is-active' : ''}`} onClick={() => handleViewModeChange('summary')}>Summary</button>
                                <button className={`pp-subtab-btn${viewMode === 'detailed' ? ' is-active' : ''}`} onClick={() => handleViewModeChange('detailed')}>Detailed</button>
                            </div>
                        </div>
                    ) : null}

                    {/* MAIN CONTENT */}
                    <div className="ar-content">
                        {/* Filter chips */}
                        {reportFilterChips.length > 0 ? (
                            <div className="ar-filter-chips">
                                {reportFilterChips.map((chip) => (<span key={chip.key} className="ar-filter-chip">{chip.label}</span>))}
                                <button className="ar-filter-chips__clear" onClick={handleClearReportFilters}>Clear All</button>
                            </div>
                        ) : null}

                        {/* Init */}
                        {status === 'init' ? (
                            <div className="ar-init-message">
                                <i className="fe fe-bar-chart-2" style={{ fontSize: '48px', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }} />
                                <p>Select a time range and click <strong>Run Report</strong> to generate your environmental report.</p>
                            </div>
                        ) : null}

                        {/* Loading */}
                        {status === 'loading' ? (
                            <div className="ar-loading"><div className="ar-loading__spinner" /><p>Loading report data...</p></div>
                        ) : null}

                        {/* Overlimit */}
                        {status === 'ready' && overLimit ? (
                            <div className="ar-overlimit">
                                <i className="fe fe-alert-triangle" style={{ fontSize: '32px', color: 'var(--color-warning)' }} />
                                <h3>Report Too Large</h3>
                                <p>This report contains more than 5,000 records.</p>
                                {csvUrl ? (
                                    <a href={csvUrl} className="dp-btn dp-btn--primary" target="_blank" rel="noopener noreferrer">
                                        <i className="fe fe-download" style={{ marginRight: '4px' }} />Download Full Report (CSV)
                                    </a>
                                ) : null}
                            </div>
                        ) : null}

                        {/* Report data */}
                        {status === 'ready' && !overLimit ? (
                            <div className="ar-detail-view">
                                {/* Controls row */}
                                <div className="ap-controls">
                                    <div className="ap-controls__left">
                                        <div className="ap-search">
                                            <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                                            </svg>
                                            <input type="search" placeholder="Search records..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="ap-search__input" aria-label="Search records" />
                                        </div>
                                    </div>
                                    <div className="ap-controls__right">
                                        {sortedData.length > 0 ? (
                                            <>
                                                <button className="rp-btn rp-btn--secondary" onClick={handleExportCsv}><i className="fe fe-download" style={{ marginRight: '4px' }} />CSV</button>
                                                <button className="rp-btn rp-btn--secondary" onClick={handleExportPdf}><i className="fe fe-file-text" style={{ marginRight: '4px' }} />PDF</button>
                                            </>
                                        ) : null}
                                    </div>
                                </div>

                                {/* Table */}
                                {totalFiltered === 0 ? (
                                    <div className="ar-init-message" style={{ paddingTop: 'var(--space-8)' }}>
                                        <i className="fe fe-inbox" style={{ fontSize: '40px', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)' }} />
                                        <p>No data to display for this report.</p>
                                    </div>
                                ) : (
                                    <EnvironmentalTable
                                        data={paginatedData}
                                        allData={sortedData}
                                        columnDefs={activeColumnDefs}
                                        visibleColumns={visibleColumns}
                                        sortColumn={sortColumn}
                                        sortOrder={sortOrder}
                                        onSort={handleSort}
                                        filters={columnFilters}
                                        onFilterChange={handleColumnFilterChange}
                                        onClearFilters={handleClearColumnFilters}
                                        showFilters={viewMode === 'detailed'}
                                        tzOffset={tzOffset}
                                    />
                                )}

                                {/* Pagination */}
                                {totalFiltered > 0 ? (
                                    <div className="ap-pagination">
                                        <p className="ap-pagination__info">
                                            Showing <span className="ap-pagination__emphasis">{showingStart}</span>&ndash;<span className="ap-pagination__emphasis">{showingEnd}</span> of{' '}
                                            <span className="ap-pagination__emphasis">{totalFiltered}</span> records
                                        </p>
                                        <div className="ap-pagination__controls">
                                            <div className="ap-per-page">
                                                <label htmlFor="er-per-page" className="ap-per-page__label">Rows per page:</label>
                                                <select id="er-per-page" value={perPage} onChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(1); }} className="ap-per-page__select">
                                                    <option value={25}>25</option>
                                                    <option value={50}>50</option>
                                                    <option value={100}>100</option>
                                                    <option value={200}>200</option>
                                                    <option value={300}>300</option>
                                                </select>
                                            </div>
                                            <nav className="ap-page-nav" aria-label="Environmental pagination">
                                                <button type="button" onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage <= 1} className="ap-page-btn" aria-label="Previous page">
                                                    <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
                                                </button>
                                                {pageNumbers.map((num) => (
                                                    <button key={num} type="button" onClick={() => setPage(num)} className={`ap-page-num${num === safePage ? ' is-active' : ''}`} aria-label={`Page ${num}`} aria-current={num === safePage ? 'page' : undefined}>{num}</button>
                                                ))}
                                                <button type="button" onClick={() => setPage(Math.min(totalPages, safePage + 1))} disabled={safePage >= totalPages} className="ap-page-btn" aria-label="Next page">
                                                    <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                                                </button>
                                            </nav>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>

                    {/* Filter Drawer */}
                    <EnvironmentalFilterDrawer
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                        onApply={handleApplyFilters}
                        onClear={handleClearReportFilters}
                        filterOptions={filterOptions}
                        currentFilters={reportFilters}
                        saving={false}
                        timeRanges={TIME_RANGES}
                        rangeId={rangeId}
                        customRange={customRange}
                        start={start}
                        end={end}
                        onTimeRangeChange={handleTimeRange}
                        onCustomToggle={handleCustomToggle}
                        onStartChange={setStart}
                        onEndChange={setEnd}
                        timeRangeDisabled={isFullBattery}
                    />

                    {/* Schedule Report Drawer */}
                    <ScheduleReportDrawer
                        open={scheduleDrawerOpen}
                        onClose={() => setScheduleDrawerOpen(false)}
                        onSave={handleScheduleSave}
                        saving={false}
                        reportTypeId={environTypeIdMap[environTypeId] || 6}
                        rangeId={rangeId}
                        rangeName={customRange ? 'Custom' : rangeName}
                        reportFilters={reportFilters}
                        filterOptions={filterOptions}
                        campusId={campusId}
                        frequencies={frequencies}
                        tzOffset={tzOffset}
                    />
                </div>
            </div>
        </>
    );
};

export default EnvironmentalReportsPage;
